import { useEffect, useState } from 'react';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useNavigate } from 'react-router-dom';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory, {
  PaginationTotalStandalone,
  PaginationProvider,
  PaginationListStandalone
} from 'react-bootstrap-table2-paginator';
import 'bootstrap/dist/css/bootstrap.min.css';
import Button from 'components/base/Button';
import { Alert, Spinner } from 'react-bootstrap';
import FilterTab from 'components/common/FilterTab';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { CustomSortCaret } from 'helpers/ui-utils';
import {
  getClientLabels,
  getClientShipmentsBySearchQuery,
  getTrackingStatus
} from 'api/api';
import SearchBox from 'components/common/SearchBox';
import Swal from 'sweetalert2';

const ClientLabel = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [error, setError] = useState([]);
  const [total, setTotal] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState('asc');
  const [columnName, setColumnName] = useState('id');
  const [sortString, setSortString] = useState('id:desc');

  useEffect(() => {
    loadData(currentPage, pageSize, 'id:desc');
  }, []);

  const loadData = async (pageNr, pageSize, sortBy) => {
    setError([]);
    setLoading(true);

    await getClientLabels(pageNr, pageSize, sortBy)
      .then(response => {
        setData(response.data.data.clients);
        setTotal(response.data.data.total);
      })
      .catch(err => {
        setLoading(false);
        try {
          if (err.response.status === 401) {
            navigate('/sign-in');
          }
          setError(err.response.data.errors);
        } catch (ex) {
          setError(['Please try again later...']);
        }
      });

    setLoading(false);
  };

  const searchData = async (pageNr, pageSize, sortBy, searchText) => {
    setError([]);
    setLoading(true);

    if (!searchText || searchText.trim() === '') {
      setError(['Enter text to search']);
    }

    await getClientShipmentsBySearchQuery(pageNr, pageSize, sortBy, searchText)
      .then(response => {
        setData(response.data.data.clients);
        setTotal(response.data.data.total);
      })
      .catch(err => {
        setLoading(false);
        try {
          if (err.response.status === 401) {
            navigate('/sign-in');
          }
          setError(err.response.data.errors);
        } catch (ex) {
          setError(['Please try again later...']);
        }
      });

    setLoading(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const CustomSortCaretUpdatedByOrder = (order, dataField) => {
    if (columnName === 'id')
      return (
        <span className="custom-sort-cart">
          <div style={{ display: 'inline-block' }}>
            ▲
            <br />▼
          </div>
        </span>
      );
    else if (sortColumn === 'asc')
      return (
        <span className="custom-sort-cart">
          <div style={{ display: 'inline-block' }}>
            <div style={{ display: 'inline-block' }}> ▲ </div>
            <br />
            <div style={{ display: 'inline-block', visibility: 'collapse' }}>
              ▼
            </div>
          </div>
        </span>
      );
    else if (sortColumn === 'desc')
      return <span className="custom-sort-cart">▼</span>;
    return null;
  };

  const handleShowStatus = row => {
    GetStatus(row.tracking_no);
  };

  const GetStatus = async trackingNr => {
    setError([]);

    await getTrackingStatus(trackingNr)
      .then(response => {
        confirmAlert(response);
      })
      .catch(err => {
        console.log(err.response);
        setError(err.response.data.errors);
      });
  };

  const confirmAlert = response => {
    const data = response.data.data;

    // Format shipping history with professional styling
    const historyHtml = data.history?.length
      ? data.history
          .map((h, index) => {
            const date = new Date(h.date).toLocaleString('en-US', {
              timeZone: 'UTC',
              year: 'numeric',
              month: 'short',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            });

            // Get status color based on status type
            const getStatusColor = status => {
              const statusLower = status.toLowerCase();
              if (statusLower.includes('delivered')) return '#28a745';
              if (
                statusLower.includes('transit') ||
                statusLower.includes('shipped')
              )
                return '#007bff';
              if (
                statusLower.includes('picked') ||
                statusLower.includes('processed')
              )
                return '#ffc107';
              if (
                statusLower.includes('exception') ||
                statusLower.includes('delayed')
              )
                return '#dc3545';
              return '#6c757d';
            };

            const statusColor = getStatusColor(h.status);
            const isLast = index === data.history.length - 1;

            return `
            <div style="
              position: relative;
              padding: 16px 0;
              border-left: ${isLast ? '3px solid ' + statusColor : '3px solid #e9ecef'};
              margin-left: 12px;
              padding-left: 24px;
              ${!isLast ? 'margin-bottom: 8px;' : ''}
            ">
              <!-- Timeline dot -->
              <div style="
                position: absolute;
                left: -8px;
                top: 20px;
                width: 14px;
                height: 14px;
                border-radius: 50%;
                background-color: ${statusColor};
                border: 3px solid white;
                box-shadow: 0 0 0 2px ${statusColor};
              "></div>
              
              <!-- Content -->
              <div style="
                background: #f8f9fa;
                border-radius: 8px;
                padding: 16px;
                border: 1px solid #e9ecef;
                box-shadow: 0 1px 3px rgba(0,0,0,0.1);
              ">
                <div style="
                  display: flex;
                  justify-content: space-between;
                  align-items: flex-start;
                  margin-bottom: 12px;
                ">
                  <div style="
                    background: ${statusColor};
                    color: white;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                  ">
                    ${h.status}
                  </div>
                  <div style="
                    color: #6c757d;
                    font-size: 13px;
                    font-weight: 500;
                  ">
                    ${date}
                  </div>
                </div>
                
                ${
                  h.location
                    ? `
                  <div style="
                    display: flex;
                    align-items: center;
                    color: #495057;
                    font-size: 14px;
                  ">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 8px;">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    ${h.location}
                  </div>
                `
                    : ''
                }
              </div>
            </div>
          `;
          })
          .join('')
      : `
        <div style="
          text-align: center;
          padding: 40px 20px;
          color: #6c757d;
        ">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin-bottom: 16px; opacity: 0.5;">
            <path d="M16 3h5v5M21 3l-7 7M13 13l7-7M8 21h8M12 17v4M5 12l-2 3h9l-2-3-5-6z"></path>
          </svg>
          <p style="margin: 0; font-size: 16px;">No shipping history available</p>
        </div>
      `;

    // Get main status color for header
    const getMainStatusColor = status => {
      const statusLower = status.toLowerCase();
      if (statusLower.includes('delivered')) return '#28a745';
      if (statusLower.includes('transit') || statusLower.includes('shipped'))
        return '#007bff';
      if (statusLower.includes('picked') || statusLower.includes('processed'))
        return '#ffc107';
      if (statusLower.includes('exception') || statusLower.includes('delayed'))
        return '#dc3545';
      return '#6c757d';
    };

    const mainStatusColor = getMainStatusColor(data.status);

    Swal.fire({
      title: '',
      html: `
      <div style="text-align: left; max-height: 70vh;  overflow-y: auto; padding-right: 8px;">
        <!-- Header Section -->
        <div style="
          background: linear-gradient(135deg, ${mainStatusColor} 0%, ${mainStatusColor}dd 100%);
          color: white;
          padding: 24px;
          border-radius: 12px;
          margin-bottom: 24px;
          text-align: center;
        ">
          <h2 style="margin: 0 0 8px 0; font-size: 24px; font-weight: 700;">
            Package Tracking
          </h2>
          <p style="margin: 0; opacity: 0.9; font-size: 14px;">
            Track your shipment progress in real-time
          </p>
        </div>

        <!-- Tracking Details -->
        <div style="
          background: #ffffff;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 24px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        ">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px;">
            <div>
              <label style="
                display: block;
                font-size: 12px;
                font-weight: 600;
                color: #6c757d;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                margin-bottom: 4px;
              ">Tracking Number</label>
              <p style="
                margin: 0;
                font-size: 16px;
                font-weight: 600;
                color: #212529;
                font-family: 'Courier New', monospace;
              ">${data.trackingNumber}</p>
            </div>
            
            <div>
              <label style="
                display: block;
                font-size: 12px;
                font-weight: 600;
                color: #6c757d;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                margin-bottom: 4px;
              ">Current Status</label>
              <div style="
                background: ${mainStatusColor};
                color: white;
                padding: 6px 12px;
                border-radius: 6px;
                font-size: 14px;
                font-weight: 600;
                display: inline-block;
              ">
                ${data.status}
              </div>
            </div>
          </div>

          ${
            data.description
              ? `
            <div style="margin-bottom: 16px;">
              <label style="
                display: block;
                font-size: 12px;
                font-weight: 600;
                color: #6c757d;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                margin-bottom: 4px;
              ">Description</label>
              <p style="margin: 0; color: #495057; line-height: 1.5;">${data.description}</p>
            </div>
          `
              : ''
          }

          ${
            data.estimatedDelivery
              ? `
            <div>
              <label style="
                display: block;
                font-size: 12px;
                font-weight: 600;
                color: #6c757d;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                margin-bottom: 4px;
              ">Estimated Delivery</label>
              <p style="
                margin: 0;
                color: #495057;
                font-size: 16px;
                font-weight: 500;
              ">${data.estimatedDelivery}</p>
            </div>
          `
              : ''
          }
        </div>

        <!-- Shipping History -->
        <div style="
          background: #ffffff;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        ">
          <h3 style="
            margin: 0 0 20px 0;
            font-size: 18px;
            font-weight: 700;
            color: #212529;
            display: flex;
            align-items: center;
          ">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 8px;">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12,6 12,12 16,14"></polyline>
            </svg>
            Shipping History
          </h3>
          
          <div style="position: relative;">
            ${historyHtml}
          </div>
        </div>
      </div>
    `,
      icon: false,
      confirmButtonText: 'Close',
      confirmButtonColor: mainStatusColor,
      width: 700,
      showCloseButton: false,
      customClass: {
        popup: 'swal-modern-popup',
        confirmButton: 'swal-modern-button'
      },
      didOpen: () => {
        // Add custom CSS for better styling
        const style = document.createElement('style');
        style.textContent = `
        .swal-modern-popup {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        }
        .swal-modern-button {
          border-radius: 8px !important;
          font-weight: 600 !important;
          padding: 12px 24px !important;
          font-size: 14px !important;
        }
      `;
        document.head.appendChild(style);
      }
    });
  };

  const columns = [
    {
      dataField: 'client.name',
      text: 'NAME',
      headerStyle: { width: '15%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'name' ? CustomSortCaretUpdatedByOrder : CustomSortCaret,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
        color: 'phoenix-gray-1100'
      }
    },
    {
      dataField: 'client.contact_name',
      text: 'CONTACT NAME',
      headerStyle: { width: '15%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'contact_name'
          ? CustomSortCaretUpdatedByOrder
          : CustomSortCaret,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
        color: 'phoenix-gray-1100'
      }
    },
    // {
    //   dataField: 'client.address',
    //   text: 'ADDRESS',
    //   headerStyle: { width: '10%', fontSize: '13px' },
    //   sort: false,
    //   sortCaret:
    //     columnName === 'address'
    //       ? CustomSortCaretUpdatedByOrder
    //       : CustomSortCaret,
    //   style: {
    //     fontSize: '12px',
    //     color: 'phoenix-gray-1100'
    //   }
    // },

    // {
    //   dataField: 'client.city',
    //   text: 'CITY',
    //   headerStyle: { width: '10%', fontSize: '13px' },
    //   sort: false,
    //   sortCaret:
    //     columnName === 'city' ? CustomSortCaretUpdatedByOrder : CustomSortCaret,
    //   style: {
    //     fontSize: '12px',
    //     color: 'phoenix-gray-1100'
    //   }
    // },
    // {
    //   dataField: 'client.state',
    //   text: 'State',
    //   headerStyle: { width: '10%', fontSize: '13px' },
    //   sort: false,
    //   sortCaret:
    //     columnName === 'state'
    //       ? CustomSortCaretUpdatedByOrder
    //       : CustomSortCaret,
    //   style: {
    //     fontSize: '12px',
    //     color: 'phoenix-gray-1100'
    //   }
    // },
    {
      dataField: 'client.zip',
      text: 'ZIP',
      headerStyle: { width: '10%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'zip' ? CustomSortCaretUpdatedByOrder : CustomSortCaret,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
        color: 'phoenix-gray-1100'
      }
    },

    {
      dataField: 'client.phone',
      text: 'PHONE',
      headerStyle: { width: '10%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'phone'
          ? CustomSortCaretUpdatedByOrder
          : CustomSortCaret,
      style: {
        fontSize: '12px',
        color: 'phoenix-gray-1100'
      }
    },

    {
      dataField: 'tracking_no',
      text: 'TRACKING NO',
      headerStyle: { width: '10%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'tracking_no'
          ? CustomSortCaretUpdatedByOrder
          : CustomSortCaret,
      style: {
        fontSize: '12px',
        color: 'phoenix-gray-1100',
        fontWeight: 'bold'
      }
    },

    {
      dataField: 'transaction_id',
      text: 'TRANSACTION ID',
      headerStyle: { width: '10%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'transaction_id'
          ? CustomSortCaretUpdatedByOrder
          : CustomSortCaret,
      style: {
        fontSize: '12px',
        color: 'phoenix-gray-1100',
        fontWeight: 'bold'
      },
      formatter: cell => {
        return cell ? `...${cell.slice(-8)}` : '';
      }
    },
    {
      dataField: 'added_date',
      text: 'DATE',
      headerStyle: { width: '10%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'added_date'
          ? CustomSortCaretUpdatedByOrder
          : CustomSortCaret,
      style: {
        fontSize: '12px',
        color: 'phoenix-gray-1100',
        fontWeight: 'bold'
      },
      formatter: cell => {
        if (!cell) return '';
        const date = new Date(cell);
        if (isNaN(date)) return ''; // Fallback for invalid date

        return date.toLocaleString('en-US', {
          timeZone: 'UTC',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
      }
    },

    // {
    //   dataField: 'status',
    //   text: 'STATUS',
    //   sort: false,
    //   sortCaret:
    //     columnName === 'status'
    //       ? CustomSortCaretUpdatedByOrder
    //       : CustomSortCaret,
    //   headerStyle: { width: '15%', fontSize: '13px' },
    //   style: {
    //     fontSize: '12px',
    //     color: 'phoenix-gray-1100'
    //   },
    //   formatter: (cell, row) => {
    //     if (row.status.name === 'Active') {
    //       return (
    //         <Badge
    //           bg={'success'}
    //           variant="phoenix"
    //           iconPosition="end"
    //           className="fs-10"
    //           icon={<FeatherIcon icon={'check'} size={12} className="ms-1" />}
    //         >
    //           {row.status.name}
    //         </Badge>
    //       );
    //     } else {
    //       return (
    //         <Badge
    //           bg={'danger'}
    //           variant="phoenix"
    //           iconPosition="end"
    //           className="fs-10"
    //           icon={<FeatherIcon icon={'info'} size={12} className="ms-1" />}
    //         >
    //           {row.status.name}
    //         </Badge>
    //       );
    //     }
    //   }
    // },

    {
      dataField: 'actions',
      text: 'Actions',
      headerStyle: { width: '20%' },
      formatter: (cell, row) => {
        return row.tracking_no ? (
          <Button variant="primary" onClick={() => handleShowStatus(row)}>
            Show Tracking Status
          </Button>
        ) : null;
      }
    }
  ];

  const handleAllClick = () => {
    setCurrentPage(1);
    loadData(1, 10, 'id:desc');
  };

  const tabItems = [
    {
      label: 'All',
      value: 'all',
      count: total,
      onClick: handleAllClick
    }
  ];

  const table = useAdvanceTable({
    data: data
  });

  const handleSearchInputChange = e => {
    if (e.target.value.length > 1) searchData(1, 10, 'id:desc', e.target.value);
    else if (e.target.value.length === 0) loadData(1, 10, 'id:desc');
  };

  const handleSearchPaste = e => {
    setTimeout(() => {
      const pastedText = e.target.value;

      if (pastedText.length > 1) searchData(1, 10, 'id:desc', pastedText);
      else if (pastedText.length === 0) loadData(1, 10, 'id:desc');
    }, 0);
  };

  const handleTableChange = (
    type,
    { page, sizePerPage, sortField, sortOrder, data }
  ) => {
    switch (type) {
      case 'pagination':
        pagination(page, sizePerPage);
        break;
      case 'sort':
        sort(sortField, sortOrder, data);
        break;
      default:
        break;
    }
  };

  const pagination = (page, sizePerPage) => {
    setCurrentPage(page);
    setPageSize(sizePerPage);
    loadData(page, sizePerPage, sortString);
  };

  const sort = sortField => {
    setCurrentPage(1);
    if (sortColumn === 'desc') {
      setSortColumn('asc');
    } else if (sortColumn === 'asc') {
      setSortColumn('desc');
    }
    switch (sortField) {
      case 'first_name':
        setColumnName('first_name');
        setSortString(`first_name:${sortColumn}`);
        loadData(1, 10, `first_name:${sortColumn}`);
        break;
      case 'last_name':
        setColumnName('last_name');
        setSortString(`last_name:${sortColumn}`);
        loadData(1, 10, `last_name:${sortColumn}`);
        break;
      case 'group':
        setColumnName('group');
        setSortString(`user_group.name:${sortColumn}`);
        loadData(1, 10, `user_group.name:${sortColumn}`);
        break;
      case 'status':
        setColumnName('status');
        setSortString(`status.name:${sortColumn}`);
        loadData(1, 10, `status.name:${sortColumn}`);
        break;
      default:
        break;
    }
  };

  return (
    <div>
      <div className="mb-9">
        <h2 className="mb-4">Shipments</h2>
        <FilterTab tabItems={tabItems} className="mb-2" />

        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap gap-3">
              <SearchBox
                placeholder="Search by Client Name or Tracking ID"
                onChange={handleSearchInputChange}
                onPaste={handleSearchPaste}
              />
              <div className="scrollbar overflow-hidden-y"></div>
              <div className="ms-xxl-auto"></div>
            </div>
          </div>

          <div>
            {Object.entries(error).length > 0 &&
              error.map((e, index) => (
                <Alert variant="soft-danger" key={index}>
                  {e}
                </Alert>
              ))}
          </div>

          <div className="bg-white border-top border-bottom border-200 position-relative top-1">
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: '200px' }}
              >
                <Spinner animation="border" role="status"></Spinner>
              </div>
            ) : (
              <PaginationProvider
                pagination={paginationFactory({
                  custom: true,
                  page: currentPage,
                  sizePerPage: pageSize,
                  totalSize: total,
                  sizePerPageList: [
                    {
                      text: '10',
                      value: 10
                    }
                  ]
                })}
              >
                {({ paginationProps, paginationTableProps }) => (
                  <div>
                    <BootstrapTable
                      remote
                      keyField="id"
                      data={data.length === 0 ? [] : data}
                      columns={columns}
                      bordered={false}
                      onTableChange={handleTableChange}
                      {...paginationTableProps}
                    />
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginTop: '-10px',
                          fontSize: '13px'
                        }}
                      >
                        <PaginationTotalStandalone {...paginationProps} />
                        <PaginationListStandalone {...paginationProps} />
                      </div>
                    </div>
                  </div>
                )}
              </PaginationProvider>
            )}
          </div>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default ClientLabel;
